import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"
import { csrfToken, formatInboxDate } from "./helpers"
import type { SelectedReply } from "./types"

type SendControlsProps = {
  readonly reply: SelectedReply
  readonly bodyHtml: string
}

type PreviewResponse = {
  readonly payload: Record<string, unknown>
  readonly would_post_to: string
  readonly stats_id_present: boolean
  readonly stats_id_source: string
  readonly draft_body_html: string
  readonly status?: string
  readonly error?: string
}

export function SendControls({ reply, bodyHtml }: SendControlsProps) {
  const { props } = usePage<PageProps>()
  const armed = props.agency?.actions_armed ?? false
  // Send transmits `bodyHtml` — the live editor content — so gate on THAT being
  // non-empty, not on the stored draft's review_status. Gating on approval forced
  // the operator to approve the stored text in order to unlock a button that then
  // sent different text; the server records the approval at send time instead.
  const sendable = bodyHtml.trim().length > 0
  const skipped = reply.active_draft?.review_status === "skipped"
  // The server refuses to send on a "sent" reply (SendService::SENDABLE_STATUSES),
  // so show the responded state instead of a Send button that can only fail. A new
  // inbound flips the status back (ingest DORMANT_STATUSES) and re-enables sending.
  const alreadySent = reply.status === "sent"
  const [preview, setPreview] = React.useState<PreviewResponse | null>(null)
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const [previewError, setPreviewError] = React.useState<string | null>(null)
  const [undoOpen, setUndoOpen] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const sendTimer = React.useRef<number | null>(null)

  React.useEffect(() => {
    return () => {
      if (sendTimer.current) window.clearTimeout(sendTimer.current)
    }
  }, [])

  const previewSend = async () => {
    setPreviewError(null)
    const form = new FormData()
    form.set("body_html", bodyHtml)
    const response = await fetch(`/replies/${reply.id}/send_preview`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() ?? "" },
      body: form,
    })
    const data = (await response.json()) as PreviewResponse
    if (!response.ok) {
      setPreviewError(data.error ?? "Preview failed")
      setPreview(null)
    } else {
      setPreview(data)
    }
    setPreviewOpen(true)
  }

  const queueSend = () => {
    if (!armed || !window.confirm("Send this reply through SmartLead?")) return

    setUndoOpen(true)
    sendTimer.current = window.setTimeout(() => {
      setUndoOpen(false)
      setSending(true)
      // The redirect carries the result as flash (FlashBanner) and refreshed
      // props: on success this whole section flips to the "Sent to ..." state.
      router.post(
        `/replies/${reply.id}/send`,
        { body_html: bodyHtml },
        { preserveScroll: true, onFinish: () => setSending(false) },
      )
    }, 3000)
  }

  const undoSend = () => {
    if (sendTimer.current) window.clearTimeout(sendTimer.current)
    sendTimer.current = null
    setUndoOpen(false)
  }

  return (
    <section className="border-t border-hairline p-4">
      <div className="flex flex-wrap items-center gap-2">
        {alreadySent ? (
          <span className="rounded-md border border-signal bg-signal-faded px-3 py-2 text-sm">
            Sent to {reply.lead_email}
            {reply.last_outbound_at ? ` · ${formatInboxDate(reply.last_outbound_at)}` : ""}
          </span>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={previewSend}>
              Preview
            </Button>
            {armed ? (
              sending ? (
                <span className="rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted">
                  Sending&hellip;
                </span>
              ) : sendable && !skipped ? (
                <Button type="button" onClick={queueSend} data-send-button>
                  <Send className="h-4 w-4" />
                  Send
                </Button>
              ) : (
                <span className="rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted">
                  {skipped ? "Draft was skipped - edit it to reopen" : "Write a reply to send"}
                </span>
              )
            ) : (
              <span className="rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted">
                Arm actions to send
              </span>
            )}
          </>
        )}
      </div>
      {undoOpen && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-signal bg-signal-faded px-3 py-2 text-sm">
          <span>Sending in 3 seconds.</span>
          <Button type="button" variant="ghost" size="sm" onClick={undoSend}>
            Undo
          </Button>
        </div>
      )}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Preview: what will be sent</DialogTitle>
            <DialogDescription>
              {preview ? `${preview.would_post_to} · stats id ${preview.stats_id_source}` : previewError}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <pre className="max-h-[60vh] overflow-auto rounded-md bg-surface p-3 text-xs">
              {JSON.stringify(preview.payload, null, 2)}
            </pre>
          )}
        </DialogContent>
      </Dialog>
    </section>
  )
}
